/**
 * Format a date string (yyyy-MM-dd or ISO) to MM/DD/YYYY for display.
 */
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '--';
  // Fast path: yyyy-MM-dd (with optional time after T)
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[2]}/${match[3]}/${match[1]}`;
  // Fallback: parse as Date object
  try {
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${mm}/${dd}/${d.getFullYear()}`;
    }
  } catch {}
  return value;
};

interface RouteTypeLookupItem {
  id: number | string;
  routeType: string;
}

/**
 * Resolve a persisted routeType value to its display label. New records store the
 * language-independent route type ID; legacy records store the label directly, so
 * unresolvable values (deleted route types, old label-based data) are returned as-is.
 */
export const resolveRouteTypeLabel = (
  rawValue: string | null | undefined,
  routeTypes: RouteTypeLookupItem[] | null | undefined,
): string => {
  if (!rawValue) return '';
  const raw = String(rawValue).trim();
  const match = routeTypes?.find(rt => String(rt.id) === raw);
  return match ? match.routeType : raw;
};

/**
 * Resolve a route-type value (ID or legacy label) to its canonical ID string, so
 * equipment/photo route-type filtering can compare like-for-like regardless of
 * whether the stored config entry predates the ID-based migration.
 */
export const resolveRouteTypeId = (
  rawValue: string | null | undefined,
  routeTypes: RouteTypeLookupItem[] | null | undefined,
): string => {
  if (!rawValue) return '';
  const raw = String(rawValue).trim();
  const byId = routeTypes?.find(rt => String(rt.id) === raw);
  if (byId) return String(byId.id);
  const byLabel = routeTypes?.find(rt => rt.routeType.trim().toLowerCase() === raw.toLowerCase());
  return byLabel ? String(byLabel.id) : raw;
};


/**
 * Transform an email or dot-separated name into a readable "First Last" display name.
 *
 * Examples:
 *   "Amit.Belavadi.Contractor@pepsico.com" → "Amit Belavadi"
 *   "john.smith@company.com"              → "John Smith"
 *   "John Smith"                          → "John Smith"
 *   "jsmith"                              → "jsmith"
 */
export const formatUserName = (value: string | null | undefined): string => {
  if (!value) return '--';
  const trimmed = value.trim();
  // Extract the local part if it's an email address
  const local = trimmed.includes('@') ? trimmed.split('@')[0] : trimmed;
  // If dot-separated, take first two parts as first name + last name
  if (local.includes('.')) {
    const parts = local.split('.').filter(Boolean);
    return parts
      .slice(0, 2)
      .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(' ');
  }
  // Already a readable name (spaces) or single token — return as-is
  return trimmed;
};

const GMT_OFFSET_TO_US_TIMEZONE: Record<string, string> = {
  'GMT-1000': 'HST',
  'GMT-0900': 'AKST',
  'GMT-0800': 'PST',
  'GMT-0700': 'MST',
  'GMT-0600': 'CST',
  'GMT-0500': 'EST',
  'GMT-0400': 'EDT',
  'GMT-0300': 'ADT',
  'GMT+0000': 'UTC',
};

const normalizeGmtOffset = (value: string): string => {
  const match = value.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?$/i);
  if (!match) return value.toUpperCase();

  const sign = match[1];
  const hours = match[2].padStart(2, '0');
  const minutes = (match[3] ?? '00').padStart(2, '0');
  return `GMT${sign}${hours}${minutes}`;
};

/**
 * Derive a short timezone abbreviation (e.g. "IST", "CST", "UTC") from a Date object.
 * toTimeString() gives something like "14:51:00 GMT+0530 (India Standard Time)";
 * we take the first letter of each word in the parenthesised name.
 * If the platform only exposes a raw GMT offset, map the common U.S. offsets
 * to their short timezone labels instead of showing GMT to users.
 */
export const getTimezoneAbbrFromTimeString = (value: string): string => {
  try {
    const nameMatch = value.match(/\(([^)]+)\)/);
    if (nameMatch) {
      const words = nameMatch[1].trim().split(/\s+/).filter(Boolean);
      if (words.length > 1) {
        // "India Standard Time" → "IST"
        // But skip generic names like "Coordinated Universal Time-09" where any
        // word contains a digit — those don't produce a meaningful abbreviation,
        // so fall through to the offset lookup instead.
        const hasDigitInWords = words.some(w => /\d/.test(w));
        if (!hasDigitInWords) {
          return words.map(w => w[0].toUpperCase()).join('');
        }
      } else if (words.length === 1 && !/\d/.test(words[0])) {
        return words[0];
      }
      // Name contained digits (e.g. "Coordinated Universal Time-09") — fall through.
    }

    // Fallback: use the raw GMT offset and translate it to a U.S. timezone label.
    // Use \d{2} for the minutes group so "GMT-08:00" is fully captured.
    const offsetMatch = value.match(/(?:GMT|UTC)[+-]\d{1,2}:?\d{2}/i);
    if (!offsetMatch) return '';

    const normalizedOffset = normalizeGmtOffset(offsetMatch[0]);
    return GMT_OFFSET_TO_US_TIMEZONE[normalizedOffset] ?? normalizedOffset;
  } catch {
    return '';
  }
};

const getTimezoneAbbr = (d: Date): string => getTimezoneAbbrFromTimeString(d.toTimeString());

/**
 * Format a UTC time value to 12-hour AM/PM in the user's local timezone,
 * followed by the timezone abbreviation, e.g. "2:51 PM IST".
 *
 * Accepts:
 *   - Time-only strings like "14:03:22.223"  (assumed UTC)
 *   - Full ISO strings    like "2026-05-19T14:03:22.223Z"
 *   - ISO without Z       like "2026-05-19T14:03:22.223" (treated as UTC)
 */
export const formatTimeAmPm = (value: string | null | undefined): string => {
  if (!value) return '--';
  try {
    const toDisplay = (d: Date): string => {
      // Some platforms (e.g. Chromium on Windows with non-IANA timezone names) embed
      // the raw GMT offset inside the toLocaleTimeString result, e.g. "4:18 AM GMT-08:00".
      // Strip any trailing GMT/UTC offset so we can append our own short abbreviation.
      const raw = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      const time = raw.replace(/\s*(?:GMT|UTC)[+-]\d{1,2}:\d{2}\b/i, '').trim();
      const abbr = getTimezoneAbbr(d);
      return abbr ? `${time} ${abbr}` : time;
    };

    // Time-only string: append today's date and Z to treat as UTC
    if (!value.includes('T') && !value.includes('-') && value.includes(':')) {
      const today = new Date().toISOString().split('T')[0];
      const d = new Date(`${today}T${value}Z`);
      if (!isNaN(d.getTime())) return toDisplay(d);
    }
    // Full ISO string — treat as UTC if no timezone offset is specified
    let normalized = value;
    if (value.includes('T') && !value.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(value)) {
      normalized = `${value}Z`;
    }
    const d = new Date(normalized);
    if (!isNaN(d.getTime())) return toDisplay(d);
    return value;
  } catch {
    return value ?? '--';
  }
};
